import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAdminAuditEntries, ADMIN_AUDIT_PAGE_SIZE, type AuditEntryRow } from '@/hooks/useAdminAuditEntries'
import { format } from 'date-fns'

const ACTION_TYPE_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'hide', label: 'Hide' },
  { value: 'warn', label: 'Warn' },
  { value: 'ban', label: 'Ban' },
  { value: 'restore', label: 'Restore' },
  { value: 'user_type_change', label: 'User type change' },
  { value: 'suspend', label: 'Suspend' },
  { value: 'unsuspend', label: 'Unsuspend' },
  { value: 'delete_user', label: 'Delete user' },
  { value: 'soft_delete_user', label: 'Soft delete user' },
  { value: 'restore_soft_deleted', label: 'Restore soft deleted' },
  { value: 'reject_deletion_request', label: 'Reject deletion request' },
  { value: 'listings_sync', label: 'Listings sync' },
]

export default function AdminAuditPage() {
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [actionType, setActionType] = useState<string>('')
  const [actorId, setActorId] = useState<string>('')
  const [page, setPage] = useState(1)

  const fromTs = dateFrom ? `${dateFrom}T00:00:00.000Z` : null
  const toTs = dateTo ? `${dateTo}T23:59:59.999Z` : null

  const { data: actors } = useQuery({
    queryKey: ['audit-actors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name')
        .or('role.eq.moderator,role.eq.admin,user_type.eq.moderator,user_type.eq.admin')
        .order('display_name', { ascending: true })
      if (error) throw error
      return (data ?? []) as { id: string; display_name: string | null }[]
    },
    staleTime: 60_000,
  })

  const { data: entries = [], isLoading, error } = useAdminAuditEntries({
    fromTs,
    toTs,
    actionType: actionType || null,
    actorId: actorId || null,
    page,
  })

  const hasNextPage = entries.length === ADMIN_AUDIT_PAGE_SIZE
  const hasPrevPage = page > 1

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <h1 id="audit-heading" className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
        Audit log
      </h1>
      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Admin and moderator actions: content moderation, user management, and listings sync. All actions are logged with actor and timestamp.
      </p>

      {/* Filters */}
      <section
        className="mb-6 p-4 rounded border-2 flex flex-wrap gap-4 items-end"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="audit-filters-heading"
      >
        <h2 id="audit-filters-heading" className="sr-only">
          Filter audit log
        </h2>
        <div>
          <label htmlFor="audit-date-from" className="block mb-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Date from
          </label>
          <input
            id="audit-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
        <div>
          <label htmlFor="audit-date-to" className="block mb-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Date to
          </label>
          <input
            id="audit-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
        <div>
          <label htmlFor="audit-action-type" className="block mb-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Action type
          </label>
          <select
            id="audit-action-type"
            value={actionType}
            onChange={(e) => {
              setActionType(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            {ACTION_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="audit-actor" className="block mb-1 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Actor
          </label>
          <select
            id="audit-actor"
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value)
              setPage(1)
            }}
            className="px-3 py-2 border-2 rounded min-w-[140px]"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="">All actors</option>
            {actors?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name || a.id}
              </option>
            ))}
          </select>
        </div>
      </section>

      {error && (
        <p role="alert" className="mb-4" style={{ color: 'var(--color-error)' }}>
          Failed to load audit log: {error instanceof Error ? error.message : String(error)}.
        </p>
      )}

      {isLoading && (
        <p style={{ color: 'var(--text-secondary)' }}>Loading audit log…</p>
      )}

      {!isLoading && !error && (
        <>
          <div className="overflow-x-auto rounded border-2" style={{ borderColor: 'var(--bg-tertiary)' }}>
            <table
              className="w-full border-collapse"
              aria-labelledby="audit-heading"
              aria-describedby="audit-table-desc"
            >
              <caption id="audit-table-desc" className="sr-only">
                Audit entries with date, action type, source, target, actor, and details
              </caption>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Date</th>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Action</th>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Source</th>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Target</th>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Actor</th>
                  <th scope="col" className="text-left py-2 px-3" style={{ color: 'var(--text-primary)' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-4 px-3 text-center" style={{ color: 'var(--text-secondary)' }}>
                      No audit entries match the filters.
                    </td>
                  </tr>
                ) : (
                  entries.map((row) => (
                    <AuditRow key={`${row.source}-${row.id}`} row={row} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <button
              type="button"
              disabled={!hasPrevPage}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 rounded border-2 disabled:opacity-50"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-label="Previous page"
            >
              Previous
            </button>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Page {page}
            </span>
            <button
              type="button"
              disabled={!hasNextPage}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-2 rounded border-2 disabled:opacity-50"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function AuditRow({ row }: { row: AuditEntryRow }) {
  const targetDesc = row.target_type && row.target_id
    ? `${row.target_type} ${row.target_id.slice(0, 8)}…`
    : '—'
  const detailsStr =
    row.details && Object.keys(row.details).length > 0
      ? JSON.stringify(row.details)
      : ''

  return (
    <tr style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
      <td className="py-2 px-3 text-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
        {format(new Date(row.created_at), 'dd/MM/yyyy HH:mm')}
      </td>
      <td className="py-2 px-3" style={{ color: 'var(--text-primary)' }}>
        {row.action_type}
      </td>
      <td className="py-2 px-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {row.source}
      </td>
      <td className="py-2 px-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {targetDesc}
      </td>
      <td className="py-2 px-3" style={{ color: 'var(--text-primary)' }}>
        {row.actor_display_name || row.actor_id || '—'}
      </td>
      <td className="py-2 px-3 text-sm max-w-xs truncate" style={{ color: 'var(--text-secondary)' }} title={detailsStr}>
        {detailsStr || '—'}
      </td>
    </tr>
  )
}
